import {
  DomainError,
  HU_VAT_RATES,
  currency,
  formatMoney,
  fromMinor,
  stockStatus,
  suggestedSalePrice,
  toMinor,
  type ArticleInput,
  type ArticleNumberKind,
  type PriceMode,
} from "@szerviz/core";
import { useCallback, useEffect, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { ErrorText } from "../components/ErrorText";
import { Field, TextInput } from "../components/Field";
import { Help } from "../components/Help";
import { useApp, useFeature } from "../context";
import type { Article, StockMovement } from "../data";

const KINDS: ArticleNumberKind[] = ["oe", "aftermarket", "ean", "other"];

const parseNumber = (s: string): number | null => {
  const t = s.replace(/\s/g, "").replace(",", ".");
  if (t === "") return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : NaN;
};

export function useStockTracking(): boolean {
  return useFeature("stock");
}

function useMoney() {
  const { i18n } = useTranslation();
  const { workshop } = useApp();
  return (minor: number | null | undefined) => (minor === null || minor === undefined ? "" : formatMoney(minor, workshop.currency, i18n.language));
}

function qty(n: number, unit?: string | null) {
  return `${Math.round(n * 1000) / 1000} ${unit ?? ""}`.trim();
}

/** Stock with its warning colour (orange below the minimum, red when out). Only a warning. */
export function StockBadge({ article, needed = 0 }: { article: Pick<Article, "trackStock" | "stock" | "minStock" | "unit">; needed?: number }) {
  const { t } = useTranslation();
  const status = stockStatus(article, needed);
  if (status === "none") return null;
  return (
    <span className={`stock stock-${status}`}>
      {t(`stock.status.${status}`, { stock: qty(article.stock ?? 0, article.unit) })}
    </span>
  );
}

export function Inventory({ openId }: { openId?: string }) {
  const { go } = useApp();
  if (openId) return <ArticlePage id={openId} onBack={() => go("inventory")} />;
  return <ArticleList />;
}

function ArticleList() {
  const { t } = useTranslation();
  const { store, workshop, go } = useApp();
  const tracking = useStockTracking();
  const money = useMoney();
  const [query, setQuery] = useState("");
  const [rows, setRows] = useState<Article[] | null>(null);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    const h = setTimeout(() => void store.searchArticles(workshop.id, query, 100).then(setRows, setError), 200);
    return () => clearTimeout(h);
  }, [store, workshop.id, query]);

  return (
    <section>
      <div className="page-head">
        <h1>
          {t("inventory.title")} <Help id="inventory.page" />
        </h1>
        <button type="button" className="primary" onClick={() => go("inventory", "new")}>
          {t("inventory.new")}
        </button>
      </div>
      {!tracking && <p className="info">{t("inventory.trackingOff")}</p>}
      <input
        type="search"
        className="search"
        placeholder={t("inventory.searchPlaceholder")}
        aria-label={t("inventory.search")}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        autoComplete="off"
      />
      <ErrorText error={error} />
      {rows === null ? (
        <p>{t("common.loading")}</p>
      ) : rows.length === 0 ? (
        <p className="muted">{t("common.noResults")}</p>
      ) : (
        <table className="list">
          <thead>
            <tr>
              <th>{t("article.name")}</th>
              <th className="num">{t("article.salePrice")}</th>
              {tracking && <th className="num">{t("article.stock")}</th>}
              <th>{t("article.brand")}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((a) => (
              <tr key={a.id} tabIndex={0} onClick={() => go("inventory", a.id)} onKeyDown={(e) => e.key === "Enter" && go("inventory", a.id)}>
                <td>
                  <span className="art-name">{a.name}</span>
                  {a.partNumber && <span className="part-no sub">{a.partNumber}</span>}
                </td>
                <td className="num">
                  {money(a.salePrice)} {a.salePrice !== null && a.salePrice !== undefined && <span className="muted small">{t(`workOrder.priceMode.${a.salePriceMode ?? "gross"}Short`)}</span>}
                </td>
                {tracking && <td className="num">{a.trackStock ? <StockBadge article={a} /> : <span className="muted">{t("article.notTracked")}</span>}</td>}
                <td>{a.brand}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

function ArticlePage({ id, onBack }: { id: string; onBack: () => void }) {
  const { t } = useTranslation();
  const { store, go } = useApp();
  const tracking = useStockTracking();
  const [article, setArticle] = useState<Article | null | undefined>(id === "new" ? null : undefined);

  const load = useCallback(async () => {
    if (id !== "new") setArticle(await store.getArticle(id));
  }, [store, id]);
  useEffect(() => void load(), [load]);

  if (article === undefined) return <p>{t("common.loading")}</p>;
  return (
    <section className="article-page">
      <div className="page-head">
        <button type="button" className="link" onClick={onBack}>
          ← {t("common.back")}
        </button>
        <h1>
          {article ? article.name : t("inventory.new")}
          {article?.partNumber && <span className="part-no sub title-sub">{article.partNumber}</span>}
        </h1>
      </div>
      {article && tracking && article.trackStock && <StockCard article={article} onChanged={load} />}
      <ArticleForm key={article?.id ?? "new"} article={article} onSaved={(a) => (id === "new" ? go("inventory", a.id) : void load())} onDeleted={onBack} />
      {article && <CrossNumbers article={article} onChanged={load} />}
    </section>
  );
}

function ArticleForm({ article, onSaved, onDeleted }: { article: Article | null; onSaved: (a: Article) => void; onDeleted: () => void }) {
  const { t } = useTranslation();
  const { store, workshop, role } = useApp();
  const tracking = useStockTracking();
  const cur = currency(workshop.currency);
  const money = useMoney();
  const toInput = (minor: number | null | undefined) => (minor === null || minor === undefined ? "" : String(fromMinor(minor, workshop.currency)));
  const defaultVat = Number(workshop.general.defaultVatRate ?? 27);
  const [partNumber, setPartNumber] = useState(article?.partNumber ?? "");
  const [name, setName] = useState(article?.name ?? "");
  const [brand, setBrand] = useState(article?.brand ?? "");
  const [category, setCategory] = useState(article?.category ?? "");
  const [unit, setUnit] = useState(article?.unit ?? "db");
  const [purchase, setPurchase] = useState(toInput(article?.purchasePrice));
  const [sale, setSale] = useState(toInput(article?.salePrice));
  const [mode, setMode] = useState<PriceMode>(article?.salePriceMode ?? "gross");
  const [vatRate, setVatRate] = useState(article?.vatRate ?? defaultVat);
  const [trackStock, setTrackStock] = useState(article?.trackStock ?? tracking);
  const [bulk, setBulk] = useState(article?.bulk ?? false);
  const [minStock, setMinStock] = useState(article?.minStock === null || article?.minStock === undefined ? "" : String(article.minStock));
  const [notes, setNotes] = useState(article?.notes ?? "");
  const [error, setError] = useState<unknown>(null);
  const [saved, setSaved] = useState(false);

  const markup = Number(workshop.general.defaultMarkupPercent ?? NaN);
  const purchaseValue = parseNumber(purchase);
  const suggestion =
    purchaseValue !== null && !Number.isNaN(purchaseValue) && purchaseValue > 0 && Number.isFinite(markup)
      ? suggestedSalePrice(toMinor(purchaseValue, workshop.currency), markup, vatRate, 10 ** cur.decimals)
      : null;

  // the name is the one required field of an article (#124): red, listed, the button grey
  const missing = name.trim() ? [] : [t("article.name")];

  const save = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSaved(false);
    if (missing.length > 0) return;
    const p = parseNumber(purchase);
    const s = parseNumber(sale);
    const m = parseNumber(minStock);
    if ([p, s, m].some((x) => x !== null && Number.isNaN(x))) return setError(new DomainError("INVALID_PRICE"));
    const input: ArticleInput = {
      partNumber, name, brand, category, unit, salePriceMode: mode, vatRate, trackStock, bulk, notes,
      purchasePrice: p === null ? null : toMinor(p, workshop.currency),
      salePrice: s === null ? null : toMinor(s, workshop.currency),
      minStock: m,
      active: article?.active ?? true,
    };
    try {
      const a = await store.saveArticle(workshop.id, input, article?.id);
      setSaved(true);
      onSaved(a);
    } catch (err) {
      setError(err);
    }
  };

  const remove = async () => {
    if (!article || !globalThis.confirm?.(t("common.confirmDelete"))) return;
    try {
      await store.deleteArticle(article.id);
      onDeleted();
    } catch (err) {
      setError(err);
    }
  };

  return (
    <form className="form-grid" onSubmit={save} noValidate>
      <TextInput label={t("article.name")} value={name} onChange={setName} required invalid={!name.trim()} autoFocus={!article} />
      <TextInput label={t("article.partNumber")} value={partNumber} onChange={setPartNumber} help="article.partNumber" />
      <TextInput label={t("article.brand")} value={brand} onChange={setBrand} />
      <TextInput label={t("article.category")} value={category} onChange={setCategory} />
      <TextInput label={t("article.unit")} value={unit} onChange={setUnit} />
      <TextInput label={`${t("article.purchasePrice")} (${cur.code})`} value={purchase} onChange={setPurchase} help="article.purchasePrice" />
      <Field label={`${t("article.salePrice")} (${cur.code})`} help="article.salePrice">
        <input value={sale} onChange={(e) => setSale(e.target.value)} aria-label={`${t("article.salePrice")} (${cur.code})`} inputMode="decimal" />
        {suggestion !== null && (
          <button type="button" className="link" onClick={() => { setSale(String(fromMinor(suggestion, workshop.currency))); setMode("gross"); }}>
            {t("article.useSuggested", { price: money(suggestion), markup: String(markup) })}
          </button>
        )}
      </Field>
      <Field label={t("workOrder.item.priceMode")}>
        <select value={mode} onChange={(e) => setMode(e.target.value as PriceMode)} aria-label={t("workOrder.item.priceMode")}>
          <option value="gross">{t("workOrder.priceMode.gross")}</option>
          <option value="net">{t("workOrder.priceMode.net")}</option>
        </select>
      </Field>
      <Field label={t("workOrder.item.vatRate")}>
        <select value={vatRate} onChange={(e) => setVatRate(Number(e.target.value))} aria-label={t("workOrder.item.vatRate")}>
          {HU_VAT_RATES.map((r) => (
            <option key={r} value={r}>
              {r}%
            </option>
          ))}
        </select>
      </Field>
      {tracking && (
        <>
          <label className="switch-row">
            <input type="checkbox" checked={trackStock} onChange={(e) => setTrackStock(e.target.checked)} aria-label={t("article.trackStock")} />
            <span>{t("article.trackStock")}</span>
            <Help id="article.trackStock" />
          </label>
          <label className="switch-row">
            <input type="checkbox" checked={bulk} onChange={(e) => { setBulk(e.target.checked); if (e.target.checked) { setUnit("l"); if (!minStock) setMinStock("10"); } }} aria-label={t("article.bulk")} />
            <span>{t("article.bulk")}</span>
            <Help id="article.bulk" />
          </label>
          <TextInput label={t("article.minStock")} value={minStock} onChange={setMinStock} help="article.minStock" />
        </>
      )}
      <Field label={t("article.notes")} wide>
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} aria-label={t("article.notes")} rows={2} />
      </Field>
      <div className="actions wide">
        <ErrorText error={error} />
        {saved && <span className="ok">{t("common.saved")}</span>}
        <button type="submit" className="primary" disabled={missing.length > 0}>
          {t("common.save")}
        </button>
        {missing.length > 0 && <span className="missing" role="status">{t("required.stillMissing", { fields: missing.join(", ") })}</span>}
        {article && (role === "owner" || role === "manager") && (
          <button type="button" className="danger" onClick={() => void remove()}>
            {t("common.delete")}
          </button>
        )}
      </div>
    </form>
  );
}

function CrossNumbers({ article, onChanged }: { article: Article; onChanged: () => Promise<void> }) {
  const { t } = useTranslation();
  const { store, workshop } = useApp();
  const [number, setNumber] = useState("");
  const [kind, setKind] = useState<ArticleNumberKind>("oe");
  const [brand, setBrand] = useState("");
  const [error, setError] = useState<unknown>(null);

  const add = async (e: FormEvent) => {
    e.preventDefault();
    if (!number.trim()) return;
    setError(null);
    try {
      await store.addArticleNumber(workshop.id, article.id, { number, kind, brand: brand.trim() || null });
      setNumber("");
      setBrand("");
      await onChanged();
    } catch (err) {
      setError(err);
    }
  };

  return (
    <section className="cross">
      <h2>
        {t("article.numbers")} <Help id="article.numbers" />
      </h2>
      {(article.numbers ?? []).length === 0 ? (
        <p className="muted">{t("article.noNumbers")}</p>
      ) : (
        <ul className="chips">
          {article.numbers!.map((n) => (
            <li key={n.id} className="num-chip">
              <span className="part-no">{n.number}</span> <span className="muted small">{t(`article.kind.${n.kind}`)}{n.brand ? ` · ${n.brand}` : ""}</span>
              <button type="button" className="link" aria-label={`${t("common.delete")}: ${n.number}`} onClick={() => void store.deleteArticleNumber(n.id).then(onChanged)}>
                ×
              </button>
            </li>
          ))}
        </ul>
      )}
      <form className="inline-form" onSubmit={add}>
        <input value={number} onChange={(e) => setNumber(e.target.value)} aria-label={t("article.number")} placeholder={t("article.number")} />
        <select value={kind} onChange={(e) => setKind(e.target.value as ArticleNumberKind)} aria-label={t("article.numberKind")}>
          {KINDS.map((k) => (
            <option key={k} value={k}>
              {t(`article.kind.${k}`)}
            </option>
          ))}
        </select>
        <input value={brand} onChange={(e) => setBrand(e.target.value)} aria-label={t("article.numberBrand")} placeholder={t("article.numberBrand")} />
        <button type="submit" disabled={!number.trim()}>
          {t("article.addNumber")}
        </button>
      </form>
      <ErrorText error={error} />
    </section>
  );
}

function StockCard({ article, onChanged }: { article: Article; onChanged: () => Promise<void> }) {
  const { t } = useTranslation();
  const { store, workshop } = useApp();
  const money = useMoney();
  const cur = currency(workshop.currency);
  const [movements, setMovements] = useState<StockMovement[] | null>(null);
  const [mode, setMode] = useState<"receipt" | "count" | null>(null);
  const [quantity, setQuantity] = useState("");
  const [cost, setCost] = useState("");
  const [supplier, setSupplier] = useState("");
  const [doc, setDoc] = useState("");
  const [note, setNote] = useState("");
  const [error, setError] = useState<unknown>(null);
  const date = (iso: string) => new Intl.DateTimeFormat(undefined, { dateStyle: "medium" }).format(new Date(iso));

  const loadMoves = useCallback(async () => setMovements(await store.stockMovements(article.id)), [store, article.id]);
  useEffect(() => void loadMoves(), [loadMoves, article.stock]);

  const q = parseNumber(quantity);
  const delta = mode === "count" && q !== null && !Number.isNaN(q) ? Math.round((q - article.stock) * 1000) / 1000 : q;
  const valid = delta !== null && !Number.isNaN(delta) && delta !== 0 && (mode === "count" ? q! >= 0 : delta > 0);

  const save = async (e: FormEvent) => {
    e.preventDefault();
    if (!valid || delta === null) return;
    setError(null);
    const c = parseNumber(cost);
    try {
      await store.addStockMovement(workshop.id, {
        articleId: article.id,
        quantity: delta,
        kind: mode === "count" ? "adjustment" : "receipt",
        unitCost: c === null || Number.isNaN(c) ? null : toMinor(c, workshop.currency),
        supplier,
        documentNo: doc,
        note: mode === "count" ? note || t("stock.countNote") : note,
      });
      setMode(null);
      setQuantity("");
      setCost("");
      setSupplier("");
      setDoc("");
      setNote("");
      await onChanged();
    } catch (err) {
      setError(err);
    }
  };

  return (
    <section className="stock-card">
      <div className="stock-now">
        <span className="muted">{t("article.stock")}</span>
        <strong className="stock-big">{qty(article.stock, article.unit)}</strong>
        <StockBadge article={article} />
      </div>
      <div className="actions">
        <button type="button" className={mode === "receipt" ? "primary" : undefined} onClick={() => setMode(mode === "receipt" ? null : "receipt")}>
          {t("stock.receipt")}
        </button>
        <button type="button" className={mode === "count" ? "primary" : undefined} onClick={() => setMode(mode === "count" ? null : "count")}>
          {t("stock.count")}
        </button>
      </div>
      {mode && (
        <form className="form-grid" onSubmit={save} noValidate>
          <TextInput label={mode === "count" ? t("stock.countedQuantity") : t("stock.receivedQuantity")} value={quantity} onChange={setQuantity} required help={mode === "count" ? "stock.count" : "stock.receipt"} autoFocus />
          {mode === "receipt" && (
            <>
              <TextInput label={`${t("stock.unitCost")} (${cur.code})`} value={cost} onChange={setCost} />
              <TextInput label={t("stock.supplier")} value={supplier} onChange={setSupplier} />
              <TextInput label={t("stock.documentNo")} value={doc} onChange={setDoc} />
            </>
          )}
          <TextInput label={t("stock.note")} value={note} onChange={setNote} wide />
          {mode === "count" && delta !== null && !Number.isNaN(delta) && quantity.trim() !== "" && (
            <p className="info wide">{t("stock.countDelta", { delta: (delta > 0 ? "+" : "") + qty(delta, article.unit) })}</p>
          )}
          <div className="actions wide">
            <ErrorText error={error} />
            <button type="submit" className="primary" disabled={!valid}>
              {mode === "count" ? t("stock.saveCount") : t("stock.saveReceipt")}
            </button>
          </div>
        </form>
      )}
      <details className="history">
        <summary>{t("stock.movements")}{movements?.length ? ` (${movements.length})` : ""}</summary>
        {movements === null ? (
          <p>{t("common.loading")}</p>
        ) : movements.length === 0 ? (
          <p className="muted">{t("stock.noMovements")}</p>
        ) : (
          <table className="list moves">
            <tbody>
              {movements.map((m) => (
                <tr key={m.id}>
                  <td>{date(m.createdAt)}</td>
                  <td>{t(`stock.kind.${m.kind}`)}{m.workOrderNumber ? ` · ${m.workOrderNumber}` : ""}</td>
                  <td className={`num ${m.quantity < 0 ? "minus" : "plus"}`}>{(m.quantity > 0 ? "+" : "") + qty(m.quantity, article.unit)}</td>
                  <td>{[m.supplier, m.documentNo, m.note].filter(Boolean).join(" · ")}{m.unitCost !== null ? ` · ${money(m.unitCost)}` : ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </details>
    </section>
  );
}
